import React from 'react'

import XMPPManager from '../util/xmppManager.js'
import WebrtcManager from '../util/webrtcManager.js'
import APIManager from '../util/apiManager.js'

import { WithDeviceContext } from './deviceContext'
import { WithCameraContext } from './cameraContext'
import { WithErrorContext } from './errorContext'

import SnapshotManager from '../util/snapshotManager'
import ErrorList from '../constants/cameraErrorList.js'
import {
    ProtoBuf,
    CameraSettings,
    CameraStatus,
    LiveControl,
} from 'alfred-protobuf'
import { TransferWithinAStationOutlined } from '@material-ui/icons'

export const IMConnectionContext = React.createContext()

class _IMConnectionProvider extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            status: 'connecting', // offline / online / reconnection
            liveCounter: 0,
            presenceList: new Set(),
            mutations: {
                initial: this._initial.bind(this),
                disconnect: this.disconnect.bind(this),
                online: this.online.bind(this),
                offline: this.offline.bind(this),
                toggleWebrtcRelay: this.toggleWebrtcRelay.bind(this),
                subscribeToXMPP: this.subscribeToXMPP.bind(this),
                unsubscribeXMPP: this.unsubscribeXMPP.bind(this),
                subscribeToCameraCtx: this.subscribeToCameraCtx.bind(this),
                unsubscribeCameraCtx: this.unsubscribeCameraCtx.bind(this),
                updateStream: this.updateStream.bind(this),
                setState: this.setState.bind(this),
            },
        }

        this.subscriber = null
        this.cameraCtxSubscriber = null
        this.webrtcSubscriber = null
        this.canvas = null
        this.apiManager = APIManager.getInstance()
        this.lastSettingFrom = { jid: '', revision: 0 }
    }

    async _initial() {
        const {
            jid,
            kvToken,
            deviceId,
            uniqueId,
            xmpp: xmppInfo,
            isFree,
        } = this.props.deviceContext

        this.xmppManager = new XMPPManager({
            jid,
            kvToken,
            deviceId,
            uniqueId,
            xmppInfo,
            isFree,
        })

        this.subscriber = this.subscribeToXMPP(this.handleXMPPMesage.bind(this))
        this.cameraCtxSubscriber = this.subscribeToCameraCtx(
            this.syncCameraRuntimeStatus.bind(this)
        )
        const snapshotManager = SnapshotManager.getInstance()
        this.generateSnapshotForXMPP = snapshotManager.generateSnapshotForXMPP

        const iceServers = await this.apiManager.getIceServers()

        this.webrtcManager = WebrtcManager.getInstance({
            iceServers,
        })

        this.subscribeToWebrtc()
        await this.xmppManager.connect()

        this.setState({
            status: 'online',
        })
    }

    async componentDidUpdate(prevProps, prevStats) {
        const { isPreview, error } = this.props.cameraContext
        const {
            error: prevError,
            isPreview: prevIsPreview,
        } = prevProps.cameraContext
        let cameraStateChanged = false

        if (prevError !== error) {
            cameraStateChanged = true

            if (error === null) {
                this.state.presenceList.forEach((to) => {
                    this.sendSnapshot({
                        to,
                    })
                })
            } else {
                if (error === ErrorList.NoCamera && this.webrtcManager) {
                    this.webrtcManager.disconnectLiveViewers({
                        close: 3,
                    })
                }
            }
        }

        if (prevIsPreview !== isPreview) {
            if (isPreview) {
                cameraStateChanged = true
                this.state.presenceList.forEach((to) => {
                    this.sendSnapshot({
                        to,
                    })
                })
            } else {
                if (this.webrtcManager) {
                    this.webrtcManager.disconnectLiveViewers({
                        close: 1,
                    })
                }
            }
        }

        if (prevStats.status !== this.state.status) {
            if (this.state.status === 'offline') {
                if (this.webrtcManager) {
                    this.webrtcManager.disconnectLiveViewers({})
                    this.setState({
                        liveCounter: this.webrtcManager.getCurrentLiveViewerCount(),
                    })
                }
            }
        }

        if (
            prevProps.cameraContext.liveStream !==
                this.props.cameraContext.liveStream ||
            prevProps.cameraContext.isAudio !==
                this.props.cameraContext.isAudio ||
            prevProps.deviceContext.isFree !== this.props.deviceContext.isFree
        ) {
            this.updateStream({
                videoStream: this.props.cameraContext.liveStream,
                isAudio: this.props.cameraContext.isAudio,
                isFree: this.props.deviceContext.isFree,
            })
            this.updateCameraLogInfo()
        }

        // detect motion status changed and notify all online viewers.
        if (
            prevProps.cameraContext.enableDetection !==
                this.props.cameraContext.enableDetection ||
            prevProps.cameraContext.detectionMode !==
                this.props.cameraContext.detectionMode ||
            prevProps.cameraContext.sensitivity !==
                this.props.cameraContext.sensitivity
        ) {
            cameraStateChanged = true
        }

        const checkList = ['isAutoLowLight', 'isAudio']
        checkList.forEach((checkItem) => {
            if (
                prevProps.cameraContext[checkItem] !==
                this.props.cameraContext[checkItem]
            ) {
                cameraStateChanged = true
            }
        })

        if (
            prevProps.cameraContext.isRecording !==
                this.props.cameraContext.isRecording ||
            prevProps.deviceContext.isZoom !== this.props.deviceContext.isZoom
        ) {
            this.updateCameraLogInfo()
        }

        if (cameraStateChanged) this.syncCameraRuntimeStatus()
    }

    syncCameraRuntimeStatus() {
        window.logManager.log(`[XMPP PB] sourceViewer >>`)
        this.state.presenceList.forEach((to) => {
            // not update the status for the viewer that make the change in the first place,
            const sourceViewer =
                to == this.lastSettingFrom.jid &&
                this.lastSettingFrom.revision + 1 ==
                    this.props.cameraContext.settingRevision
            // NOTE: judge the xmpp client is viewer or not by jid (https://alfredlabs.atlassian.net/wiki/spaces/SPEC/pages/1507402/JID+Specification+V1)
            const isViewer = to.includes('/ivuu2') || to.includes('/ivuu3')
            window.logManager.log(
                `[XMPP PB] sourceViewer ${sourceViewer}, isViewer: ${isViewer}, to:${to}`
            )
            if (!sourceViewer && isViewer) {
                this.sendCameraStatus({
                    to,
                    id: 0,
                })
            }
        })
    }

    updateCameraLogInfo() {
        if (this.webrtcManager) {
            const liveViewerList = this.webrtcManager.getCurrentLiveViewerList()
            Object.keys(liveViewerList).map((viewerJid) =>
                this.sendLiveSessionStatus({
                    to: viewerJid,
                    groupID: liveViewerList[viewerJid].groupID,
                    id: 0,
                })
            )
        }
    }

    subscribeToWebrtc() {
        const subscriber = this.webrtcManager.observable.subscribe(
            this.handleWebrtcMessage.bind(this)
        )
        this.webrtcSubscriber = subscriber
    }

    subscribeToXMPP(func) {
        const subscriber = this.xmppManager.observable.subscribe(func)
        return subscriber
    }

    unsubscribeXMPP(subscriber) {
        this.xmppManager.observable.unsubscribe(subscriber)
    }

    subscribeToCameraCtx(func) {
        const subscriber = this.props.cameraContext.observable.subscribe(func)
        return subscriber
    }

    unsubscribeCameraCtx(subscriber) {
        this.props.cameraContext.observable.unsubscribe(subscriber)
    }

    online() {
        if (this.state.status === 'offline') {
            this.setState({
                status: 'online',
            })
        }
    }

    offline() {
        if (this.state.status !== 'offline') {
            this.setState({
                status: 'offline',
            })
        }
    }

    async disconnect() {
        this.setState({
            status: 'offline',
        })

        if (this.xmppManager) {
            await this.xmppManager.close(this.subscriber)
        }

        if (this.webrtcManager) {
            this.webrtcManager.close(this.webrtcSubscriber)
        }
    }

    toggleWebrtcRelay(isRelay) {
        this.webrtcManager.toggleIsRelay(isRelay)
    }

    async handleWebrtcMessage(message) {
        const to = message.viewerJid

        switch (message.key) {
            case 'connected':
                const {
                    previewWidth,
                    previewHeight,
                    mutations: { updateStatus },
                } = this.props.cameraContext

                const { isFree, uniqueId, groupId } = this.props.deviceContext

                if (this.props.cameraContext.liveWidth !== message.liveWidth) {
                    if (message.liveWidth === 1280) {
                        if (previewWidth === 1280) {
                            updateStatus({
                                liveWidth: previewWidth,
                                liveHeight: previewHeight,
                            })
                        }
                    } else {
                        updateStatus({
                            liveWidth: previewWidth,
                            liveHeight: previewHeight,
                        })
                    }
                }
                this.sendLiveSessionStatus({
                    to,
                    groupID: message.groupID,
                    id: 0,
                })
                this.sendCameraStatus({ to, id: 0 })

                window.eventManager.sendLiveEvent({
                    isFree,
                    uniqueId,
                    groupId,
                })
                break
            case 'terminateViewerWebrtcConnection':
                this.xmppManager.terminateViewerWebrtcConnection({
                    to,
                    close: message.close,
                    ssid: message.ssid,
                })
                break
            case 'sendCandidate':
                this.xmppManager.sendIceCandidate({
                    to,
                    candidate: message.candidate,
                })
                break
            default:
                break
        }
    }

    async handleXMPPMesage(message) {
        const to = message.viewerJid
        const {
            mutations: { updateStatus },
        } = this.props.cameraContext
        const { jid, isFree, isZoom } = this.props.deviceContext

        if (to === jid) {
            return
        }

        const { email } = this.props.deviceContext
        window.logManager.log(
            `[XMPP PB] handleXMPP settingRevision: ${this.props.cameraContext.settingRevision}`
        )

        switch (message.key) {
            case 'online':
                this.setState({
                    status: 'online',
                })
                break
            case 'connecting':
                this.setState({
                    status: 'connecting',
                })
                break
            case 'disconnect':
                this.setState({
                    status: 'offline',
                })
                break
            case 'offline':
                if (message.data === 'conflict') {
                    await this.xmppManager.close()
                    await this.props.errorContext.pushErrorMessage({
                        errorKey: 'xmppConnectionConflict',
                        replaceArgs: {
                            email,
                        },
                        action1: () => {
                            window.close()
                        },
                    })
                } else {
                }
                this.setState({
                    status: 'offline',
                })
                break
            case 'reloadFeature':
                await this.props.deviceContext.mutations.reloadFeature()
                // TODO: reloadFeature() will trigger page reload, so OpResult won't able to execute in time
                this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.OK,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: 'reloadFeature',
                    id: message.id,
                })
                break
            case 'checkLiveViewerSharePermission':
                const { uniqueId } = this.props.deviceContext
                // viewer call delete api and reload xmpp command simutaneously
                // have to wait for some time to check in order to avoid conflict
                setTimeout(() => {
                    if (this.webrtcManager) {
                        this.webrtcManager.checkLiveViewerSharePermission({
                            uniqueId,
                        })
                    }
                }, 1000)
                break
            case 'setPreview':
                this.lastSettingFrom = {
                    jid: to,
                    revision: this.props.cameraContext.settingRevision,
                }
                updateStatus({
                    isPreview: message.enabled,
                })
                this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.OK,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: '',
                    id: message.id,
                })
                break
            case 'setZoomInLock':
                this.lastSettingFrom = {
                    jid: to,
                    revision: this.props.cameraContext.settingRevision,
                }
                this.props.deviceContext.mutations.updateStatus({
                    zoomInLock: message.zoomInLock,
                })
                // HACK: manual udpate setting revision for fields stored in deviceContext
                updateStatus({
                    settingRevision:
                        this.props.cameraContext.settingRevision + 1,
                })
                this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.OK,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: '',
                    id: message.id,
                })
                this.state.presenceList.forEach((to) => {
                    this.sendCameraStatus({ to, id: message.id })
                })
                break
            case 'zoom':
                if (isFree) {
                    return
                }

                // andorid viewer will send scale = 0 when user double tap to zoom
                const doubleTapScale = isZoom ? 1000 : 2000
                const scale =
                    message.scale >= 1000 ? message.scale : doubleTapScale

                // Zoom 2.0
                this.props.deviceContext.mutations.updateStatus({
                    clickX: message.coord_x / 1000,
                    clickY: message.coord_y / 1000,
                    zoomMode: 2,
                    zoomScale: scale / 1000,
                    isZoom: message.zoom_in,
                })
                break
            case 'setResolution':
                const { previewWidth, previewHeight } = this.props.cameraContext
                this.lastSettingFrom = {
                    jid: to,
                    revision: this.props.cameraContext.settingRevision,
                }

                if (message.resolution === 1) {
                    // ResolutionState.PRSET1
                    updateStatus({
                        liveWidth: previewWidth / 4,
                        liveHeight: previewHeight / 4,
                        resolutionPreset: 1,
                    })
                    if (!isFree && previewWidth === 640) {
                        updateStatus({
                            liveWidth: previewWidth / 2,
                            liveHeight: previewHeight / 2,
                            resolutionPreset: 1,
                        })
                    }
                } else if (message.resolution === 2) {
                    // ResolutionState.PRSET2
                    updateStatus({
                        liveWidth: previewWidth / 2,
                        liveHeight: previewHeight / 2,
                        resolutionPreset: 2,
                    })
                    if (!isFree && previewWidth === 640) {
                        updateStatus({
                            liveWidth: previewWidth,
                            liveHeight: previewHeight,
                            resolutionPreset: 2,
                        })
                    }
                } else {
                    // ResolutionState.AUTO
                    updateStatus({
                        liveWidth: previewWidth,
                        liveHeight: previewHeight,
                        resolutionPreset: 0,
                    })
                }
                await this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.OK,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: `Update resolution to: ${this.props.cameraContext.liveWidth}x${this.props.cameraContext.liveHeight}`,
                    id: message.id,
                })
                break
            case 'setNight':
                let opResultCode = ProtoBuf.ErrorCode.INVALID_ARGUMENT
                let opResultMessage = `Unknown low-light/night mode: ${message.night}`

                if (0 <= message.night && message.night <= 2) {
                    this.lastSettingFrom = {
                        jid: to,
                        revision: this.props.cameraContext.settingRevision,
                    }

                    switch (message.night) {
                        case 1:
                            updateStatus({
                                isAutoLowLight: false,
                                isNightVision: true,
                            })
                            break
                        case 2:
                            updateStatus({
                                isAutoLowLight: true,
                                isNightVision: true,
                            })
                            break
                        case 0:
                        default:
                            updateStatus({
                                isAutoLowLight: false,
                                isNightVision: false,
                            })
                    }

                    opResultCode = ProtoBuf.ErrorCode.OK
                    opResultMessage = `Update low-light/night mode to: ${message.night}`
                }

                this.xmppManager.sendOpResult({
                    to,
                    code: opResultCode,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: opResultMessage,
                    id: message.id,
                })

                break
            case 'setMicrophone':
                this.lastSettingFrom = {
                    jid: to,
                    revision: this.props.cameraContext.settingRevision,
                }
                updateStatus({ isAudio: message.audio_enabled })
                this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.OK,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: `Update microphone state: ${message.audio_enabled}`,
                    id: message.id,
                })
                break
            case 'setAlias':
                this.lastSettingFrom = {
                    jid: to,
                    revision: this.props.cameraContext.settingRevision,
                }
                // HACK: manual udpate setting revision for fields stored in deviceContext
                updateStatus({
                    settingRevision:
                        this.props.cameraContext.settingRevision + 1,
                })
                this.props.deviceContext.mutations.updateStatus({
                    label: message.cameraAlias,
                })
                this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.OK,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: ``,
                    id: message.id,
                })
                break
            case 'getPresence':
                this.handlePresence.call(this, message.data)
                break
            case 'getSnapshot':
                this.sendSnapshot.call(this, { to, id: message.id })
                break
            case 'getCameraDeviceCapability':
                await this.xmppManager.sendCameraCapability({
                    to,
                    id: message.id,
                })
                break
            case 'getMotionStatus':
                this.sendCameraStatus({ to, id: message.id })
                break
            case 'getCameraSettings':
                this.sendCameraSetting.call(this, { to, id: message.id })
                break
            case 'setMotionStatus':
                this.lastSettingFrom = {
                    jid: to,
                    revision: this.props.cameraContext.settingRevision,
                }
                const { motionStatus } = message
                const { detectionMode, sensitivity } = motionStatus

                if (0 <= detectionMode && detectionMode <= 2) {
                    if (
                        detectionMode === CameraSettings.DetectionMode.DISABLED
                    ) {
                        this.props.cameraContext.mutations.updateStatus({
                            enableDetection: false,
                            sensitivity: Number(sensitivity),
                        })
                    } else if (
                        detectionMode === CameraSettings.DetectionMode.MOTION
                    ) {
                        this.props.cameraContext.mutations.updateStatus({
                            enableDetection: true,
                            detectionMode: 'motion',
                            sensitivity: Number(sensitivity),
                        })
                    } else if (
                        detectionMode === CameraSettings.DetectionMode.PERSON
                    ) {
                        this.props.cameraContext.mutations.updateStatus({
                            enableDetection: true,
                            detectionMode: 'person',
                            sensitivity: Number(sensitivity),
                        })
                    }

                    await this.xmppManager.sendOpResult({
                        to,
                        code: ProtoBuf.ErrorCode.OK,
                        settingRevision: this.props.cameraContext
                            .settingRevision,
                        message: `Update detection to: ${detectionMode}/${sensitivity}`,
                        id: message.id,
                    })
                } else {
                    await this.xmppManager.sendOpResult({
                        to,
                        code: ProtoBuf.ErrorCode.INVALID_ARGUMENT,
                        settingRevision: this.props.cameraContext
                            .settingRevision,
                        message: `Unknown detection mode: ${detectionMode}`,
                        id: message.id,
                    })
                }
                break
            case 'getCameraStatus':
            case 'getCameraRuntimeStatus':
                this.sendCameraStatus.call(this, {
                    to,
                    id: message.id,
                    isRespond: true,
                })
                break
            case 'getLiveSessionStatus':
                this.sendLiveSessionStatus.call(this, {
                    to,
                    groupID: null,
                    id: 0,
                })
                break
            case 'getCameraLog':
                this.sendCameraLog({ to, id: message.id })
                break
            case 'getOffer':
                const {
                    liveStream,
                    isLived,
                    mutations,
                } = this.props.cameraContext
                const groupID = await this.handleGetOffer({
                    to,
                    sdp: message.sdp,
                    videoStream: liveStream,
                })
                if (!isLived) {
                    mutations.updateStatus({
                        isLived: true,
                    })
                }
                break
            case 'getRemoteCandidate':
                this.handleGetCandidate({
                    to,
                    candidate: message.candidate,
                })
                break
            case 'closeWebrtcConntection': {
                this.closeWebrtcConnection({ to })
                this.exitLive()

                break
            }
            case 'updateViewerCapability':
                this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.OK,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: '',
                    id: message.id,
                })
                break
            case 'unimplemented':
                this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.UNIMPLEMENTED,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: '',
                    id: message.id,
                })
                break
            case 'invalidArgument':
                this.xmppManager.sendOpResult({
                    to,
                    code: ProtoBuf.ErrorCode.INVALID_ARGUMENT,
                    settingRevision: this.props.cameraContext.settingRevision,
                    message: '',
                    id: message.id,
                })
                break
            default:
                break
        }
    }

    exitLive() {
        // reset live resolution
        const { previewWidth, previewHeight } = this.props.cameraContext
        const { isFree, zoomInLock } = this.props.deviceContext

        this.props.cameraContext.mutations.updateStatus({
            liveWidth: previewWidth / (isFree ? 2 : 1),
            liveHeight: previewHeight / (isFree ? 2 : 1),
        })

        if (!zoomInLock) {
            this.props.deviceContext.mutations.updateStatus({
                isZoom: false,
                clickX: 0,
                clickY: 0,
                zoomMode: 1,
                zoomScale: 1,
            })
        }
        this.setState({
            liveCounter: this.webrtcManager.getCurrentLiveViewerCount(),
        })
    }

    handlePresence(data) {
        const from = data.attrs.from
        const { jid } = this.props.deviceContext

        let status = data.attrs.type === 'unavailable' ? 'offline' : 'online'
        if (
            status === 'online' &&
            !this.state.presenceList.has(from) &&
            from !== jid
        ) {
            this.state.presenceList.add(from)

            this.sendCameraStatus({ to: from, id: 0 })
        }
        if (status === 'offline') {
            this.state.presenceList.delete(from)
            if (this.webrtcManager) {
                const isViewerOffline = this.webrtcManager.disconnectIfLiveViewerOffline(
                    from
                )
                if (isViewerOffline) {
                    this.exitLive()
                }
            }
        }
    }

    closeWebrtcConnection({ to }) {
        this.webrtcManager.disconnect(to)
    }

    async handleGetCandidate({ to, candidate }) {
        this.webrtcManager.addIceCandidate({
            viewerJid: to,
            candidate,
        })
    }

    async updateStream({ videoStream, isAudio, isFree }) {
        if (this.webrtcManager) {
            await this.webrtcManager.updateStream({
                videoStream,
                isAudio,
                isFree,
            })
        }
    }

    async handleGetOffer({ to, sdp, videoStream }) {
        try {
            const isWebviewer = checkIsWebviewer(to)

            const { isFree, uniqueId, email } = this.props.deviceContext
            const { isAudio, isPreview } = this.props.cameraContext
            const getMotionStatus = () => {
                const { enableDetection } = this.props.cameraContext
                return enableDetection
            }
            const {
                sdp: localSDP,
                groupID,
            } = await this.webrtcManager.setOffer({
                offer: sdp,
                videoStream,
                viewerJid: to,
                isWebviewer,
                isFree,
                isPreview,
                isAudio,
                uniqueId,
                email,
                getMotionStatus,
            })
            const answer = JSON.stringify({
                type: 'answer',
                sdp: localSDP,
            })
            await this.xmppManager.acceptConnection({
                to,
                answer,
            })

            this.setState({
                liveCounter: this.webrtcManager.getCurrentLiveViewerCount(),
            })

            return groupID
        } catch (error) {
            if (error === 'CameraDisable') {
                this.setState({
                    liveCounter: this.webrtcManager.getCurrentLiveViewerCount(),
                })
            }
        }
    }

    async sendCameraLog({ to, id }) {
        if (this.webrtcManager) {
            const liveViewerList = this.webrtcManager.getCurrentLiveViewerList()
            if (liveViewerList[to] !== undefined) {
                const groupID = liveViewerList[to].groupID
                const logJSON = JSON.stringify({ groupID })
                this.xmppManager.sendCameraLogResponse({
                    to,
                    id,
                    jsonStr: logJSON,
                })
            } else {
                window.logManager.error(
                    `Fail to find groupID for ${to} while sending caerma log`
                )
            }
        }
    }

    async sendSnapshot({ to, id }) {
        try {
            const { isFree } = this.props.deviceContext
            const { mainCanvas } = this.props.cameraContext
            const snapshotManager = SnapshotManager.getInstance()
            const image = await snapshotManager.generateSnapshotForXMPP({
                isFree,
                recordCanvas: mainCanvas,
            })
            await this.xmppManager.sendSnapshot({
                image,
                to,
                id,
            })
        } catch (error) {
            window.logManager.error(error)
        }
    }

    async sendCameraSetting({ to, id }) {
        const { label, isFree, zoomInLock } = this.props.deviceContext

        const {
            isAudio,
            isGrantedMicrophonePermission,
            isPreview,
            isNightVision,
            isAutoLowLight,
            sensitivity,
            detectionMode,
            enableDetection,
            settingRevision,
        } = this.props.cameraContext

        let lowLightMode = CameraSettings.LowLightFilterMode.OFF
        if (isNightVision) {
            lowLightMode = isAutoLowLight
                ? CameraSettings.LowLightFilterMode.AUTO
                : CameraSettings.LowLightFilterMode.ON
        }
        const sensitivityMap = {
            1: CameraSettings.Sensitivity.LOW,
            2: CameraSettings.Sensitivity.MEDIUM,
            3: CameraSettings.Sensitivity.HIGH,
        }

        let detectionModeField =
            detectionMode == 'motion'
                ? CameraSettings.DetectionMode.MOTION
                : CameraSettings.DetectionMode.PERSON
        let detectionModeSetting = {
            enabled: enableDetection,
            mode: enableDetection
                ? detectionModeField
                : CameraSettings.DetectionMode.DISABLED,
            sensitivity: sensitivityMap[sensitivity],
        }

        let settings = {
            settingsRevision: settingRevision,
            cameraLensSetting: { lensId: 1 },
            cameraEnabledSetting: { enabled: isPreview },
            cameraAliasSetting: { alias: label },
            lowlightFilterSetting: {
                mode: lowLightMode,
                profile: CameraSettings.LowLightFilterProfile.DEFAULT, // WebCamera has no lowlight profile
            },
            accessPrioritySetting: {
                mode: 0,
            },
            detectionModeSetting: detectionModeSetting,
            autoPowerSavingSetting: { enabled: false },
            micOff: isGrantedMicrophonePermission ? !isAudio : undefined,
            cameraMicrophoneSetting: { enabled: false },
            rotationSetting: { degree: 0 },
            zoomInLockSetting: { enabled: zoomInLock },
        }
        await this.xmppManager.sendCameraSetting({
            to,
            settingProtoObj: settings,
            id,
        })
    }

    async sendLiveSessionStatus({ to, groupID, id }) {
        const {
            isAudio,
            isPreview,
            isNightVision,
            isAutoLowLight,
            liveWidth,
            liveHeight,
            resolutionPreset,
            isRecording,
            previewWidth,
        } = this.props.cameraContext
        const {
            isFree,
            isZoom,
            zoomScale,
            zoomMode,
            clickX,
            clickY,
        } = this.props.deviceContext

        let lowLightMode = CameraSettings.LowLightFilterMode.OFF
        if (isNightVision) {
            lowLightMode = isAutoLowLight
                ? CameraSettings.LowLightFilterMode.AUTO
                : CameraSettings.LowLightFilterMode.ON
        }
        const logs = { groupID }
        const zoom_state = isZoom ? 4 : 2

        const status = {
            featureAvail: {
                resolutionChange: isFree ? -1 : 1,
                zoom: isFree ? -1 : zoom_state,
                // ZOOM_OUT = 0;
                // ZOOM_DOUBLE_TAP = 1;
                // ZOOM_2_0_OUT = 2;
                // ZOOM_2_0_DOUBLE_TAP = 3;
                // ZOOM_2_0_PINCH = 4;
                // https://github.com/alfred-systems/iVuu/blob/fa9b863df31ad9bc7d2ff5bbb3be3cdcdffc6fab/app/src/main/java/com/ivuu/Constant.java#L224
            },
            micSetting: {
                enabled: isAudio,
            },
            lowlightSetting: {
                mode: lowLightMode,
                profile: CameraSettings.LowLightFilterProfile.DEFAULT,
            },
            width: liveWidth,
            height: liveHeight,
            recordingState: {
                recording: isRecording,
            },
            zoomState: {
                coordX: Math.floor(clickX * 1000),
                coordY: Math.floor(clickY * 1000),
                scale: Math.floor(zoomScale * 1000), // 1x zoom, scale = 1000, 2x zoom scale = 2000
                zoomIn: isZoom,
            },
            resolutionState: resolutionPreset,
            // R.id.txt_quality_hd -> PRESET_3,
            // R.id.txt_quality_vga -> PRESET_2
            // ref: https://github.com/alfred-systems/iVuu/blob/e3b0137d7d223612f9cdafa98a856bde2586f029/app/src/main/java-v21/com/alfredcamera/ui/viewer/live/LiveActivity.kt#L353
            logs: JSON.stringify(logs),
        }

        await this.xmppManager.sendLiveSessionStatus({
            to,
            status,
            id,
        })
    }

    async sendCameraStatus({ to, id, isRespond }) {
        const { jid, isFree, zoomInLock } = this.props.deviceContext

        const {
            isAutoLowLight,

            detectionMode,
            enableDetection,
            sensitivity,

            isPreview,
            isAudio,
            isGrantedMicrophonePermission,

            error,
            settingRevision,
        } = this.props.cameraContext

        const noCamera =
            error === ErrorList.NoCamera ||
            error === ErrorList.NotGrandVideoPermission
        let issueDetected = false
        if (error === ErrorList.NoCamera) {
            issueDetected = true
        }
        if (error === ErrorList.NotGrandVideoPermission) {
            issueDetected = true
        }
        if (!isPreview) {
            issueDetected = true
        }

        const status = {
            settingsRevision: settingRevision,
            disabled: !isPreview,
            micOff: isGrantedMicrophonePermission ? !isAudio : undefined,
            connectedUsersList: [...this.state.presenceList.values()],
            statusCodesList: !noCamera ? [] : [4002], // currently only handle "no camera permission" error
            membershipStatus: isFree
                ? CameraStatus.Membership.FREE
                : CameraStatus.Membership.PREMIUM,
            detectorStatus: {
                mode: enableDetection
                    ? CameraSettings.DetectionMode.DISABLED
                    : detectionMode === 'motion'
                    ? CameraSettings.DetectionMode.MOTION
                    : CameraSettings.DetectionMode.PERSON,
            },
            healthStatus: {
                issueDetected: issueDetected,
            },
        }

        if (isRespond !== undefined) {
            await this.xmppManager.sendCameraStatusResponse({
                to,
                deviceInfoProtoObj: status,
                id,
            })
        } else {
            await this.xmppManager.sendCameraStatus({
                to,
                deviceInfoProtoObj: status,
                id,
            })
        }
    }

    render() {
        return (
            <IMConnectionContext.Provider value={this.state}>
                {this.props.children}
            </IMConnectionContext.Provider>
        )
    }
}

function checkIsWebviewer(jid) {
    return /webviewer/.test(jid)
}

export const IMConnectionProvider = WithErrorContext(
    WithCameraContext(WithDeviceContext(_IMConnectionProvider))
)

export const WithIMConnectionContext = (Component) => (props) => (
    <IMConnectionContext.Consumer>
        {(context) => <Component imConnectionContext={context} {...props} />}
    </IMConnectionContext.Consumer>
)
